import { useEffect, useState } from "react";
import api from "@/lib/api";
import { UploadCloud, Database, FileText, Sparkles } from "lucide-react";
import useAutoFill from "@/hooks/useAutoFill";
import AutoFilledHint from "@/components/AutoFilledHint";
import DrillholeImportWizard from "@/components/DrillholeImportWizard";
import DrillholeLogPanel from "@/components/DrillholeLogPanel";
import StageModeSwitcher, { useStageMode } from "@/components/StageModeSwitcher";

export default function Stage2DrillholeImport({ projectId, onCompleted }) {
  const [_smMode, _setSmMode] = useStageMode(projectId, 2, "ai");
  const [count, setCount] = useState(null);
  const [holes, setHoles] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logHoleId, setLogHoleId] = useState(null);
  // sourceStamp: recomputed each time `holes.length` OR the sum of assay
  // counts changes — this triggers the debounced backend re-fetch so the
  // auto-fill hints never lag the on-screen row-count (spec rule #3).
  const sourceStamp = `${holes.length}:${holes.reduce((s,h)=>s+(h.assays?.length||0),0)}`;
  const auto = useAutoFill(projectId, { sourceStamp });

  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}/drillholes`).then(({ data }) => {
      setHoles(data || []);
      setCount(data?.length ?? 0);
      // Session I-32 warmup — kick off Claude for the newest 5 holes in
      // the background so any subsequent "Analyze" click resolves instantly
      // from cache. Fire-and-forget — never blocks the UI.
      if ((data?.length || 0) > 0) {
        api.post(`/projects/${projectId}/drillholes/ai-narrative/warmup?limit=5`).catch(() => {});
      }
    }).catch(() => setCount(0));
  }, [projectId]);

  const totalMeters = holes.reduce((s, h) => s + (h.collar?.depth || 0), 0);
  const holeCountF = auto.field("hole_count");
  const depthRangeF = auto.field("depth_range");
  const spacingF = auto.field("spacing_stats");
  const commodityF = auto.field("commodity");
  const crsF = auto.field("coordinate_system");
  const extentF = auto.field("spatial_extent");

  return (
    <div className="space-y-6" data-testid="stage2-content">
      <div className="flex items-center justify-between gap-3 pb-2 border-b border-neutral-100">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-medium">Input mode</div>
        <StageModeSwitcher mode={_smMode} onChange={_setSmMode} testidPrefix="stage2-mode" />
      </div>
      <div className="grid grid-cols-4 gap-3">
        <KPI label="Drillholes" value={count ?? "…"} testid="stage2-kpi-count"
             hint={holeCountF && <AutoFilledHint reason={holeCountF.reason} source={holeCountF.source} compact />} />
        <KPI label="Total metres logged" value={totalMeters ? totalMeters.toFixed(1) : "—"} testid="stage2-kpi-meters" mono
             hint={depthRangeF && <AutoFilledHint reason={`hole depths ${depthRangeF.value.min}–${depthRangeF.value.max} m · median ${depthRangeF.value.median}`} source={depthRangeF.source} compact />} />
        <KPI label="Assays" value={holes.reduce((s,h)=>s+(h.assays?.length||0),0)} testid="stage2-kpi-assays" mono
             hint={commodityF && <AutoFilledHint reason={`primary element: ${commodityF.value} · ${commodityF.reason}`} source={commodityF.source} compact />} />
        <KPI label="Lithology intervals" value={holes.reduce((s,h)=>s+(h.lithology?.length||0),0)} testid="stage2-kpi-liths" mono />
      </div>

      {/* AI-detected data-driven summary — auto-fills only when supported by data */}
      {(spacingF || extentF || crsF) && (
        <div className="border border-primary/30 bg-primary/[0.03] rounded p-3 space-y-2" data-testid="stage2-auto-summary">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.14em] text-primary">AI · detected from your data</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[11.5px] font-mono">
            {spacingF && (
              <div>
                <span className="text-muted-foreground">Nearest-neighbour collar spacing</span>
                <div className="tabular-nums">
                  min {spacingF.value.nn_min}m · median <strong className="text-foreground">{spacingF.value.nn_median}m</strong> · max {spacingF.value.nn_max}m
                </div>
                <AutoFilledHint reason={spacingF.reason} source={spacingF.source} compact />
              </div>
            )}
            {extentF && (
              <div data-testid="stage2-auto-extent">
                <span className="text-muted-foreground">Collar extent</span>
                <div className="tabular-nums">
                  X {extentF.value.minx.toFixed(0)}–{extentF.value.maxx.toFixed(0)} · Y {extentF.value.miny.toFixed(0)}–{extentF.value.maxy.toFixed(0)}
                </div>
                <AutoFilledHint reason={extentF.reason} source={extentF.source} compact />
              </div>
            )}
            {crsF && (
              <div data-testid="stage2-auto-crs">
                <span className="text-muted-foreground">Coordinate system (heuristic)</span>
                <div className="tabular-nums"><strong className="text-foreground">{crsF.value}</strong></div>
                <AutoFilledHint reason={crsF.reason} source={crsF.source} compact />
              </div>
            )}
            {depthRangeF && (
              <div data-testid="stage2-auto-depth">
                <span className="text-muted-foreground">End-of-hole depth range</span>
                <div className="tabular-nums">
                  {depthRangeF.value.min}m → <strong className="text-foreground">{depthRangeF.value.max}m</strong> · median {depthRangeF.value.median}m
                </div>
                <AutoFilledHint reason={depthRangeF.reason} source={depthRangeF.source} compact />
              </div>
            )}
          </div>
        </div>
      )}

      <div className="border border-dashed border-neutral-300 rounded-lg p-8 text-center bg-neutral-50">
        <UploadCloud className="h-8 w-8 text-neutral-500 mx-auto mb-3"/>
        <p className="text-[13px] text-neutral-800 font-medium">Import collar · survey · assay · lithology</p>
        <p className="text-[11.5px] text-neutral-500 mt-1">
          CSV / DAT / LAS / STR with column-mapping wizard.
        </p>
        <button type="button"
                onClick={() => setWizardOpen(true)}
                data-testid="stage2-open-wizard"
                className="inline-flex items-center gap-1.5 mt-4 text-[11.5px] text-[#4CAF50] hover:text-[#2E7D32] border border-[#4CAF50]/40 rounded px-3 py-1.5">
          <Database className="h-3.5 w-3.5"/> Open import wizard
        </button>
      </div>

      {/* Iter I-27 · fix nested-Dialog anti-pattern flagged by test agent.
          DrillholeImportWizard renders its OWN <Dialog> at top level, so
          we drive it via open/onOpenChange props — no outer wrapper. */}
      <DrillholeImportWizard
        projectId={projectId}
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        data-testid="stage2-wizard-dialog"
        onImported={() => {
          // Refresh KPIs + close the wizard on a successful commit.
          setWizardOpen(false);
          api.get(`/projects/${projectId}/drillholes`).then(({ data }) => {
            setHoles(data || []);
            setCount(data?.length ?? 0);
          }).catch(() => {});
          if (onCompleted) onCompleted();
        }}
      />

      {holes.length > 0 && (
        <div className="border border-neutral-200 rounded overflow-hidden">
          <table className="w-full text-[12px] font-mono">
            <thead className="bg-white text-[10px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="text-left px-3 py-2">Hole ID</th>
                <th className="text-right px-3 py-2">X</th>
                <th className="text-right px-3 py-2">Y</th>
                <th className="text-right px-3 py-2">Z</th>
                <th className="text-right px-3 py-2">Depth</th>
                <th className="text-right px-3 py-2">Assays</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {holes.slice(0, 25).map((h) => (
                <tr key={h.id} className="border-t border-neutral-200 hover:bg-white/30">
                  <td className="px-3 py-1.5">{h.hole_id}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{h.collar?.x?.toFixed(1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{h.collar?.y?.toFixed(1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{h.collar?.z?.toFixed(1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{h.collar?.depth?.toFixed(1)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{h.assays?.length || 0}</td>
                  <td className="px-3 py-1.5 text-right">
                    <button
                      onClick={() => { setLogHoleId(h.id); setLogOpen(true); }}
                      data-testid={`stage2-log-open-${h.hole_id}`}
                      className="inline-flex items-center gap-1 text-[10.5px] text-[#4CAF50] hover:text-[#2E7D32] border border-[#4CAF50]/40 rounded px-2 py-0.5 mr-1"
                      title="Open drillhole log · AI analyze"
                    >
                      <Sparkles className="h-3 w-3" /> Analyze
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {holes.length > 25 && (
            <div className="px-3 py-2 text-[10.5px] text-neutral-500 border-t border-neutral-200 bg-neutral-50">
              Showing 25 of {holes.length} — click any <span className="text-[#4CAF50]">Analyze</span> button to open the full log & AI narrative for that hole.
            </div>
          )}
        </div>
      )}

      <DrillholeLogPanel
        projectId={projectId}
        holes={holes}
        initialHoleId={logHoleId}
        open={logOpen}
        onOpenChange={setLogOpen}
      />
    </div>
  );
}

function KPI({ label, value, mono, testid, hint }) {
  return (
    <div className="border border-neutral-200 rounded-md px-4 py-3.5 bg-neutral-50" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.12em] text-neutral-500">{label}</div>
      <div className={`text-[26px] font-medium mt-1 ${mono ? "font-mono tabular-nums" : ""}`}>{value}</div>
      {hint}
    </div>
  );
}
